
/**
 * Gallery Navigation Handler with Drag Support
 * Manages horizontal navigation controls and mouse drag for the image gallery
 * Based on ColorCardNavigation pattern but adapted for gallery layout
 */
class GalleryNavigation {
  constructor() {
    this.container = null;
    this.leftButton = null;
    this.rightButton = null;
    this.scrollAmount = 240; // Two cards width (120px + gap)
    this.dragHandler = null;
    
    console.log('GalleryNavigation: Constructor called');
    this.init();
  }

  init() {
    console.log('GalleryNavigation: Init called, document ready state:', document.readyState);
    this.container = document.querySelector('[data-gallery]');
    this.leftButton = document.querySelector('[data-gallery-nav-left]');
    this.rightButton = document.querySelector('[data-gallery-nav-right]');

    console.log('GalleryNavigation: Elements found:', {
      container: !!this.container,
      leftButton: !!this.leftButton,
      rightButton: !!this.rightButton,
      containerElement: this.container,
      leftButtonElement: this.leftButton,
      rightButtonElement: this.rightButton
    });

    if (!this.container || !this.leftButton || !this.rightButton) {
      console.warn('GalleryNavigation: Required elements not found, retrying in 500ms');
      setTimeout(() => this.init(), 500);
      return;
    }

    console.log('GalleryNavigation: All elements found, setting up event listeners and drag support');
    this.setupEventListeners();
    this.setupDragScrolling();
    this.updateButtonVisibility();
  }

  setupDragScrolling() {
    // Initialize drag scroll handler for gallery
    if (window.DragScrollHandler && this.container) {
      this.dragHandler = new window.DragScrollHandler(this.container, {
        scrollAmount: 1.5, // Slightly higher sensitivity for gallery
        enableVertical: false // Only horizontal scrolling for gallery
      });
      console.log('GalleryNavigation: Drag scrolling enabled');
    } else {
      console.log('GalleryNavigation: DragScrollHandler not available, retrying in 200ms');
      setTimeout(() => this.setupDragScrolling(), 200);
    }
  }

  setupEventListeners() {
    console.log('GalleryNavigation: Setting up event listeners');
    
    // Navigation button clicks
    this.leftButton.addEventListener('click', () => {
      console.log('GalleryNavigation: Left button clicked');
      this.scrollLeft();
    });
    this.rightButton.addEventListener('click', () => {
      console.log('GalleryNavigation: Right button clicked');
      this.scrollRight();
    });

    // Update button visibility on scroll
    this.container.addEventListener('scroll', () => {
      console.log('GalleryNavigation: Container scrolled');
      this.updateButtonVisibility();
    });

    // Update on resize
    window.addEventListener('resize', () => {
      console.log('GalleryNavigation: Window resized');
      this.updateButtonVisibility();
    });

    // Watch for gallery content changes
    const observer = new MutationObserver(() => {
      console.log('GalleryNavigation: Content changed');
      this.updateButtonVisibility();
    });

    observer.observe(this.container, {
      childList: true,
      subtree: true
    });

    console.log('GalleryNavigation: Event listeners set up successfully');
  }

  scrollLeft() {
    if (this.container) {
      console.log('GalleryNavigation: Scrolling left by', this.scrollAmount);
      this.container.scrollBy({
        left: -this.scrollAmount,
        behavior: 'smooth'
      });
    }
  }

  scrollRight() {
    if (this.container) {
      console.log('GalleryNavigation: Scrolling right by', this.scrollAmount);
      this.container.scrollBy({
        left: this.scrollAmount,
        behavior: 'smooth'
      });
    }
  }

  updateButtonVisibility() {
    if (!this.container || !this.leftButton || !this.rightButton) {
      console.warn('GalleryNavigation: Missing elements in updateButtonVisibility');
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = this.container;
    const maxScroll = scrollWidth - clientWidth;

    console.log('GalleryNavigation: Updating button visibility', {
      scrollLeft,
      scrollWidth,
      clientWidth,
      maxScroll,
      hasOverflow: maxScroll > 0
    });

    // Show/hide left button
    if (scrollLeft <= 0) {
      console.log('GalleryNavigation: Hiding left button (at start)');
      this.leftButton.classList.add('hidden');
    } else {
      console.log('GalleryNavigation: Showing left button');
      this.leftButton.classList.remove('hidden');
    }

    // Show/hide right button
    if (scrollLeft >= maxScroll - 1) { // -1 for floating point precision
      console.log('GalleryNavigation: Hiding right button (at end)');
      this.rightButton.classList.add('hidden');
    } else {
      console.log('GalleryNavigation: Showing right button');
      this.rightButton.classList.remove('hidden');
    }

    // Hide both buttons if no scroll is needed
    if (maxScroll <= 0) {
      console.log('GalleryNavigation: No overflow, hiding both buttons');
      this.leftButton.classList.add('hidden');
      this.rightButton.classList.add('hidden');
    }
  }

  // Method to refresh navigation when gallery content changes
  refresh() {
    console.log('GalleryNavigation: Refresh called');
    this.updateButtonVisibility();
  }

  // Method to destroy the navigation (cleanup)
  destroy() {
    console.log('GalleryNavigation: Destroy called');
    
    // Cleanup drag handler
    if (this.dragHandler) {
      this.dragHandler.destroy();
      this.dragHandler = null;
    }

    // Remove event listeners
    if (this.leftButton) {
      this.leftButton.removeEventListener('click', this.scrollLeft);
    }
    if (this.rightButton) {
      this.rightButton.removeEventListener('click', this.scrollRight);
    }
    if (this.container) {
      this.container.removeEventListener('scroll', this.updateButtonVisibility);
    }
    window.removeEventListener('resize', this.updateButtonVisibility);
  }
}

// Export for use in other scripts
window.GalleryNavigation = GalleryNavigation;

// Auto-initialize gallery navigation when DOM is ready
console.log('GalleryNavigation: Script loaded, auto-initializing...');
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    console.log('GalleryNavigation: DOM ready, initializing');
    new GalleryNavigation();
  });
} else {
  console.log('GalleryNavigation: DOM already ready, initializing now');
  new GalleryNavigation();
}
